import * as React from 'react';
import { handleErrorResponse, handleSuccessfulResponse } from '../../utils/network/utils';
import type { OAuthProviderEnum, PlatformsEnum } from '../../utils/types/eunms';
import type { ResponseResult } from '@utils';
import { NetworkClientBuilder } from '@utils';

export type CognitoContextType = {
	credentialsSignup: (
		email: string,
		password: string,
		firstName: string,
		lastName: string
	) => Promise<ResponseResult<any>>;
	credentialsLogin: (email: string, password: string) => Promise<ResponseResult<any>>;
	confirmSignup: (email: string, password: string, code: string) => Promise<ResponseResult<any>>;
	resendConfirmationCode: (email: string) => Promise<ResponseResult<any>>;
	forgotPassword: (email: string) => Promise<ResponseResult<any>>;
	resetPassword: (email: string, code: string, newPassword: string) => Promise<ResponseResult<any>>;
	changePassword: (email: string, password: string) => Promise<ResponseResult<any>>;
	ssoAction: (provider: OAuthProviderEnum) => Promise<string>;
	ssoRedirectAction: (provider: string, returnUrl: string, loginHint?: string) => Promise<string>;
	host: string;
};

export const CognitoContext = React.createContext<CognitoContextType | null>(null);

type Props = {
	host: string;
	errors: {
		generic: string;
	};
	platform: PlatformsEnum;
};

export const CognitoProvider: React.FC<Props> = ({ host, children, errors, platform }) => {
	const { current: network } = React.useRef(
		NetworkClientBuilder.create(undefined)
			.addResponseInterceptor({
				onResponse: handleSuccessfulResponse,
				onError: handleErrorResponse(errors),
			})
			.build()
	);

	const credentialsSignup = React.useCallback(
		async (email: string, password: string, firstName: string, lastName: string): Promise<ResponseResult<any>> => {
			const url: string = `${host}/signup`;

			return await network.post(url, { email, password, firstName, lastName, platform });
		},
		[host, network, platform]
	);

	const credentialsLogin = React.useCallback(
		async (email: string, password: string): Promise<ResponseResult<any>> => {
			const url: string = `${host}/login`;

			return await network.post(url, { email, password, platform });
		},
		[host, network, platform]
	);

	const confirmSignup = React.useCallback(
		async (email: string, password: string, code: string): Promise<ResponseResult<any>> => {
			const url: string = `${host}/signup/confirm`;

			return await network.put(url, { email, password, code, platform });
		},
		[host, network, platform]
	);

	const resendConfirmationCode = React.useCallback(
		async (email: string): Promise<ResponseResult<any>> => {
			const url: string = `${host}/signup/send-confirmation-code`;

			return await network.post(url, { email, platform });
		},
		[host, network, platform]
	);

	const forgotPassword = React.useCallback(
		async (email: string): Promise<ResponseResult<any>> => {
			const url: string = `${host}/forget-password`;

			return await network.post(url, { email, platform });
		},
		[host, network, platform]
	);

	const resetPassword = React.useCallback(
		async (email: string, code: string, newPassword: string): Promise<ResponseResult<any>> => {
			const url: string = `${host}/reset-password`;

			return await network.patch(url, { email, code, password: newPassword, platform });
		},
		[host, network, platform]
	);

	const changePassword = React.useCallback(
		async (email: string, password: string) => {
			const url: string = `${host}/login/change-temporary-password`;

			return await network.patch(url, { email, password, platform });
		},
		[host, network, platform]
	);

	const ssoAction = React.useCallback(
		async (provider: OAuthProviderEnum): Promise<string> => {
			const win = window.open('about:blank', '_blank');

			if (!win) {
				throw new Error('Popup blocked. Please allow popups for this site.');
			}

			const response: ResponseResult<{ redirectUri: string; stateId: string }> = await network.get(
				`${host}/oauth?provider=${encodeURIComponent(provider)}&platform=${encodeURIComponent(platform)}`
			);

			if (response.error) {
				throw new Error(response.error.message);
			}

			win.location.href = response.result!.redirectUri as string;

			return response.result!.stateId;
		},
		[host, network, platform]
	);

	const ssoRedirectAction = React.useCallback(
		async (provider: string, returnUrl: string, loginHint?: string): Promise<string> => {
			const params = new URLSearchParams({
				provider,
				platform,
				returnUrl,
			});
			const trimmedHint = loginHint?.trim();
			if (trimmedHint) {
				params.set('loginHint', trimmedHint);
			}

			return `${host}/oauth/browser-redirect?${params.toString()}`;
		},
		[host, platform]
	);

	return (
		<CognitoContext.Provider
			value={{
				credentialsSignup,
				credentialsLogin,
				confirmSignup,
				resendConfirmationCode,
				forgotPassword,
				resetPassword,
				changePassword,
				ssoAction,
				ssoRedirectAction,
				host,
			}}
		>
			{children}
		</CognitoContext.Provider>
	);
};

export const useCognitoContext = () => {
	const context = React.useContext(CognitoContext);

	if (!context) {
		throw new Error('useCognitoContext must be used within CognitoProvider');
	}

	return context;
};
